/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { organizationService } from '../../services/api';
import { finopsService } from '../../services/finopsApi';
import { Organization, OrganizationMember, OrgRole } from '../../types';

const MANAGER_ROLES: OrgRole[] = ['org_owner', 'org_admin', 'org_billing_admin'];

export default function FinopsAccessTab({ org }: { org: Organization }) {
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [grantedUserIds, setGrantedUserIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [membersRes, opsRes] = await Promise.all([
        organizationService.getMembers(org.id),
        finopsService.listOperators(org.id),
      ]);
      setMembers(membersRes.data.members || []);
      setGrantedUserIds(new Set((opsRes.data.operators || []).map((o) => o.user_id)));
    } catch {
      toast.error('Failed to load FinOps access');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (member: OrganizationMember, on: boolean) => {
    setBusyUserId(member.user_id);
    // optimistic
    setGrantedUserIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(member.user_id);
      else next.delete(member.user_id);
      return next;
    });
    try {
      if (on) await finopsService.grantOperator(org.id, member.user_id);
      else await finopsService.revokeOperator(org.id, member.user_id);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Update failed');
      load(); // revert to server truth
    } finally {
      setBusyUserId(null);
    }
  };

  if (loading) {
    return <div className="text-gray-400 text-sm">Loading…</div>;
  }

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-white mb-1">FinOps operator access</h3>
      <p className="text-sm text-gray-400 mb-4">
        Operators can verify connections and run inventory/cost syncs and cost analysis. Owners, admins
        and billing admins are operators automatically; grant the operator role to other members below.
      </p>
      <ul className="divide-y divide-gray-700">
        {members.map((m) => {
          const isManagerMember = MANAGER_ROLES.includes(m.role);
          const checked = isManagerMember || grantedUserIds.has(m.user_id);
          return (
            <li key={m.user_id} className="flex items-center justify-between py-3">
              <div>
                <p className="text-sm text-white">{m.user?.name || m.user?.email || m.user_id}</p>
                <p className="text-xs text-gray-500">{m.role}</p>
              </div>
              {isManagerMember ? (
                <span className="text-xs text-gray-400">Operator (via role)</span>
              ) : (
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={busyUserId === m.user_id}
                    onChange={(e) => toggle(m, e.target.checked)}
                  />
                  <span className="text-xs text-gray-400">Operator</span>
                </label>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
